/**
 * IndividualGates — gate profile for `Individual` customerType.
 *
 * Ported from SPFx ProceduralGates:
 *   - _validateRequiredFields (Individual/NonCitizen path, INDIVIDUAL_FIELDS)
 *   - _validateEntityRestrictions (no restrictions for plain Individual)
 *   - _validateAuthorizationGates (N/A)
 *   - _validateRelationships (N/A baseline; joint handled in JointIndividualGates)
 *
 * Plus v3 additions for the compliance backbone: CIP + OFAC + CDD + W-9.
 */

import type { CartSnapshot, GateResult } from '../workflow-engine/types';
import {
  buildCipGate,
  buildOfacGate,
  buildRequiredFieldGates,
  getPrimary,
  passGate,
  type FieldSpec,
} from '../ProceduralGates';

import { REQUIRED_INDIVIDUAL_FIELDS } from './requiredIndividualFields';

// Single source of truth (Bill 2026-07-23) — shared with the person-modal
// save gate and GateQuickFixModal so the layers can never disagree again.
const INDIVIDUAL_FIELDS: ReadonlyArray<FieldSpec> =
  REQUIRED_INDIVIDUAL_FIELDS.map((f) => ({ field: f.field, label: f.label }));

export function runIndividualGates(cart: CartSnapshot): GateResult[] {
  const gates: GateResult[] = [];
  const primary = getPrimary(cart);
  if (!primary) return gates; // engine already pushed CART_NO_PEOPLE

  // Required fields (primary only — joint handled in JointIndividualGates)
  gates.push(...buildRequiredFieldGates(cart, primary, INDIVIDUAL_FIELDS));

  // Non-resident alien — country of citizenship required (SPFx NONCIT_COUNTRY)
  if (primary.citizenshipStatus === 'Non-Resident Alien' && !primary.countryOfCitizenship) {
    gates.push({
      gateCode: 'NONCIT_COUNTRY',
      entityType: cart.entityType ?? '',
      category: 'requiredField',
      status: 'fail',
      label: 'Country of citizenship required',
      reason: 'Non-Resident Alien applicants require a country of citizenship.',
      remediationAction: { label: 'Set citizenship', deepLinkStepId: 'compliance', fieldId: 'countryOfCitizenship' },
    });
  }

  // CIP + OFAC + CDD
  gates.push(buildCipGate(cart));
  gates.push(buildOfacGate(cart));

  // CDD acknowledgement (SPFx treated CDD gate as pending until exempt or answered)
  if (primary.cddExempt) {
    gates.push({
      gateCode: 'CDD',
      entityType: cart.entityType ?? '',
      category: 'compliance',
      status: 'pass',
      label: 'Customer Due Diligence (CDD)',
      reason: `Auto-exempt (reason code ${primary.cddExemptReasonCode ?? '-'}).`,
    });
  } else {
    gates.push({
      gateCode: 'CDD',
      entityType: cart.entityType ?? '',
      category: 'compliance',
      status: primary.citizenshipStatus ? 'pass' : 'fail',
      label: 'Customer Due Diligence (CDD)',
      reason: primary.citizenshipStatus
        ? undefined
        : 'CDD requires citizenship status to be set for the primary applicant.',
      remediationAction: primary.citizenshipStatus
        ? undefined
        : { label: 'Complete CDD', deepLinkStepId: 'compliance', fieldId: 'citizenshipStatus' },
    });
  }

  // W-9/W-8 + USA PATRIOT disclosure gates REMOVED 2026-06-15 (Bill): they were
  // unwired TODO-stubs (DocumentGeneratorService / DisclosurePackageService not
  // ported) that rendered as permanent "warn" rows and falsely implied the cart
  // was incomplete. Do NOT emit a gate for infrastructure that doesn't exist yet.
  // Re-introduce as real gates (status driven by collected docs / delivered
  // disclosure bundle) only when those services land in v3.1.

  // Signature card placeholder — generated by DocumentGeneratorService at commit.
  gates.push(passGate('SIG_CARD_GENERATED', cart.entityType ?? '', 'documentCollection',
    'Signature card (auto-generated at commit)'));

  return gates;
}
